/**
 * Selbstverbesserung, Phase 1 „Vorschlagen“ — die gemeinsamen Regeln. (17.10.2026, Plan genehmigt von Martin 05:17, NUR Phase 1)
 *
 * Jede Nacht sammelt der Server Zahlen und Kurztitel (server/selbstverbesserung-quellen.ts), das starke Modell wählt daraus
 * höchstens drei Vorschläge (server/selbstverbesserung-lauf.ts), Martin sagt in den Einstellungen Ja / Nein / Später. Gebaut wird
 * NICHTS automatisch — ein „Ja“ heißt nur: der Schmied darf es aufgreifen (scripts/selbstverbesserung.mjs).
 *
 * Hier steht, was Server, Seite und Skript gleich lesen müssen: der Kontoschalter, die Klassen, die Status und ihre Wechsel,
 * und die strenge Prüfung der Modellausgabe. Alles rein, ohne DOM und ohne Datenbank.
 *
 * ⚠️ KLASSE C wird NIE vorgeschlagen (Plan: Außenwirkung, Geld, Schlüssel, Löschen, Privatsphäre, Sicherheit, Zimmermann,
 *    Adressat). Liefert das Modell eine, fällt sie heraus; nennt eine A/B-Beschreibung ein Wort aus `KLASSE_C_WOERTER`, ebenso.
 * ⚠️ Die Modellausgabe ist DATEN: sie wird nur geprüft und als kurzer Text gespeichert, nie ausgeführt.
 */

import { nenntPrivates } from "./privat-woerter.js";

/** Der Schlüssel in den Einstellungen des Kontos. Werte: "aus" (Vorgabe) | "vorschlagen". */
export const SELBSTVERBESSERUNG_FELD = "selbstverbesserung";

export type SelbstverbesserungModus = "aus" | "vorschlagen";

/** Nur Konto 1 (Betreiber) — wie der Dreier-Modus. */
const KONTO = 1;

export function selbstverbesserungFuerKonto(userId: number | null | undefined): boolean {
  return userId === KONTO;
}

/** Der Modus aus dem rohen Kontowert: nur genau "vorschlagen" in Konto 1 schaltet ein. Rein. */
export function selbstverbesserungModus(wert: unknown, userId: number | null | undefined): SelbstverbesserungModus {
  return selbstverbesserungFuerKonto(userId) && wert === "vorschlagen" ? "vorschlagen" : "aus";
}

/** Der Notschalter der Umgebung: nur "0" schaltet ab. */
export function selbstverbesserungNotschalterAus(env: Readonly<Record<string, string | undefined>>): boolean {
  return (env.SELBSTVERBESSERUNG ?? "").trim() === "0";
}

export const KLASSEN = ["A", "B", "C"] as const;
export type Klasse = (typeof KLASSEN)[number];
export const AUFWAENDE = ["S", "M", "L"] as const;
export type Aufwand = (typeof AUFWAENDE)[number];
export const QUELL_ARTEN = ["postfach", "kennzahl", "ticket", "thema"] as const;
export type QuellArt = (typeof QUELL_ARTEN)[number];

export const STATUS = ["vorschlag", "ja", "nein", "spaeter", "gebaut"] as const;
export type Status = (typeof STATUS)[number];

/** Was Martin antworten kann. */
export const ANTWORTEN = ["ja", "nein", "spaeter"] as const;
export type Antwort = (typeof ANTWORTEN)[number];

export function istAntwort(v: unknown): v is Antwort {
  return typeof v === "string" && (ANTWORTEN as readonly string[]).includes(v);
}

/**
 * Erlaubte Wechsel. Martin: vorschlag/später → ja | nein | später. Der Schmied: ja → gebaut (Skript `erledigt`).
 * Nein und gebaut sind endgültig. Rein.
 */
export function wechselErlaubt(von: Status, nach: Status): boolean {
  if (von === nach) return false;
  if (von === "vorschlag" || von === "spaeter") return nach === "ja" || nach === "nein" || nach === "spaeter";
  // Ein Ja ist zurücknehmbar (ja → nein | später), solange nicht gebaut (Prüfauflage 17.10.).
  if (von === "ja") return nach === "gebaut" || nach === "nein" || nach === "spaeter";
  return false;
}

/** Höchstens so viele Vorschläge je Nacht. */
export const VORSCHLAEGE_MAX = 3;
export const TITEL_MAX = 80;
export const BESCHREIBUNG_MAX = 200;
export const BELEG_MAX = 60;

/**
 * Wörter, an denen eine Beschreibung als Klasse C gilt — auch wenn das Modell „A“ oder „B“ schreibt. Klein geschrieben; ein
 * Treffer am Wortanfang reicht („Schlüsselablage“, „löschen“). Bewusst grob: lieber ein Vorschlag zu wenig.
 */
export const KLASSE_C_WOERTER: readonly string[] = [
  // Außenwirkung
  "mail", "telegram", "newsletter", "webseite", "website", "veröffentlich", "publish", "live schalten", "webhook", "slack",
  "empfänger", "empfaenger",
  // Geld und Kosten
  "geld", "faktura", "rechnung", "zahlung", "iban", "guthaben", "kosten", "bezahlt", "api",
  // Schlüssel und Zugänge
  "schlüssel", "schluessel", "token", "passwort", "kennwort", "zugang", "secret", "auth", "anmeldung", "login",
  // Löschen und Daten
  "lösch", "loesch", "leeren", "truncate", "migration", "migrier", "delete",
  // Privatsphäre, Adressat, Zimmermann
  "privat", "datenschutz", "adressat", "weiche", "hörstrom", "hoerstrom", "zimmermann", "gesundheit", "medikament",
  // Sicherheit, Ausliefern
  "sicherheit", "injektion", "fremdtext", "sperre", "deploy",
];

/** Nur als ganzes Wort (sonst träfe „drop“ das „Dropdown“). */
const KLASSE_C_GANZ: readonly string[] = ["drop"];

const escRe = (w: string): string => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const KLASSE_C_MUSTER = new RegExp(
  [
    ...KLASSE_C_WOERTER.map((w) => `(?<!\\p{L})${escRe(w)}`),
    ...KLASSE_C_GANZ.map((w) => `(?<!\\p{L})${escRe(w)}(?!\\p{L})`),
  ].join("|"),
  "iu",
);

/**
 * Trifft der Text ein Wort der Klasse C — oder etwas Privates (shared/privat-woerter.ts)? Jede Stelle zählt, nicht nur die
 * erste. Ergänzt nach der Prüfung 17.10. (Auflage 4). Rein.
 */
export function nenntKlasseC(text: string): boolean {
  const t = text ?? "";
  return KLASSE_C_MUSTER.test(t) || nenntPrivates(t);
}

/** Ein geprüfter Vorschlag, bereit zum Speichern. */
export interface GepruefterVorschlag {
  titel: string;
  beschreibung: string;
  klasse: Exclude<Klasse, "C">;
  aufwand: Aufwand;
  nutzen: number;
  beleg: string;
  quelle: QuellArt;
  belegZahl: number | null;
}

/** Ein „braucht Martin“-Hinweis (Klasse C) — nur Titel und Beleg, nie ein Bauplan, nie gespeichert als Vorschlag. */
export interface CHinweis {
  titel: string;
  beleg: string;
}

export interface PruefErgebnis {
  ok: boolean;
  vorschlaege: GepruefterVorschlag[];
  hinweise: CHinweis[];
  /** Warum ganz verworfen (nur feste Worte). */
  grund?: "kein-json" | "falsche-form" | "zu-viele" | "leer";
  /** Wie viele Einträge einzeln verworfen wurden. */
  verworfen: number;
}

/** Steuer-, Format- und Richtungszeichen weg, Leerraum zusammen, gekappt. Rein. */
export function saeubereKurz(s: string, max: number): string {
  return s
    .replace(/[\p{Cc}\p{Cf}\u2028\u2029]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

/** Das erste JSON-Objekt aus der Modellantwort (auch in ```json-Zaun). `null`: keines. Rein. */
export function jsonAus(roh: string): unknown {
  const t = (roh ?? "").trim();
  const zaun = /```(?:json)?\s*([\s\S]*?)```/i.exec(t);
  const kern = (zaun ? zaun[1] : t) ?? "";
  const a = kern.indexOf("{");
  const b = kern.lastIndexOf("}");
  if (a < 0 || b <= a) return null;
  try {
    return JSON.parse(kern.slice(a, b + 1));
  } catch {
    return null;
  }
}

/**
 * Die Modellausgabe streng prüfen. Erwartet:
 *   { "vorschlaege": [ { titel, beschreibung, klasse, aufwand, nutzen, beleg } ], "braucht_martin": [ { titel, beleg } ] }
 * `belege`: die Kennungen, die wir dem Modell gegeben haben → Art und Zahl. Ein Vorschlag ohne bekannte Kennung fällt heraus.
 *
 * Ganz verworfen (nichts gespeichert): kein JSON, falsche Form, mehr als VORSCHLAEGE_MAX Einträge. Einzelne Einträge mit
 * falschen Feldern oder Klasse C fallen heraus; Klasse C wird zum Hinweis (nur Titel + Beleg). Rein.
 */
export function pruefeModellAusgabe(
  roh: string,
  belege: ReadonlyMap<string, { quelle: QuellArt; zahl: number | null }>,
): PruefErgebnis {
  const leer = (grund: PruefErgebnis["grund"]): PruefErgebnis => ({ ok: false, vorschlaege: [], hinweise: [], grund, verworfen: 0 });
  const j = jsonAus(roh);
  if (!j || typeof j !== "object" || Array.isArray(j)) return leer("kein-json");
  const o = j as Record<string, unknown>;
  const liste = o.vorschlaege;
  if (!Array.isArray(liste)) return leer("falsche-form");
  if (liste.length > VORSCHLAEGE_MAX) return leer("zu-viele");
  const vorschlaege: GepruefterVorschlag[] = [];
  const hinweise: CHinweis[] = [];
  let verworfen = 0;
  const gesehen = new Set<string>();
  for (const e of liste) {
    const v = einVorschlag(e, belege);
    if (v === "c") {
      const x = e as Record<string, unknown>;
      const titel = typeof x.titel === "string" ? saeubereKurz(x.titel, TITEL_MAX) : "";
      const beleg = typeof x.beleg === "string" ? x.beleg.trim() : "";
      if (titel && !nenntPrivates(titel) && belege.has(beleg)) hinweise.push({ titel, beleg });
      verworfen++;
      continue;
    }
    if (!v || gesehen.has(v.titel.toLowerCase())) {
      verworfen++;
      continue;
    }
    gesehen.add(v.titel.toLowerCase());
    vorschlaege.push(v);
  }
  const bm = o.braucht_martin;
  if (Array.isArray(bm)) {
    for (const e of bm.slice(0, VORSCHLAEGE_MAX)) {
      if (!e || typeof e !== "object") continue;
      const x = e as Record<string, unknown>;
      const titel = typeof x.titel === "string" ? saeubereKurz(x.titel, TITEL_MAX) : "";
      const beleg = typeof x.beleg === "string" ? x.beleg.trim() : "";
      if (titel && !nenntPrivates(titel) && belege.has(beleg)) hinweise.push({ titel, beleg });
    }
  }
  return { ok: true, vorschlaege, hinweise: hinweise.slice(0, VORSCHLAEGE_MAX), verworfen, ...(vorschlaege.length ? {} : { grund: "leer" as const }) };
}

function einVorschlag(
  e: unknown,
  belege: ReadonlyMap<string, { quelle: QuellArt; zahl: number | null }>,
): GepruefterVorschlag | "c" | null {
  if (!e || typeof e !== "object" || Array.isArray(e)) return null;
  const x = e as Record<string, unknown>;
  if (typeof x.titel !== "string" || typeof x.beschreibung !== "string" || typeof x.beleg !== "string") return null;
  const klasse = x.klasse;
  if (klasse === "C") return "c";
  if (klasse !== "A" && klasse !== "B") return null;
  if (typeof x.aufwand !== "string" || !(AUFWAENDE as readonly string[]).includes(x.aufwand)) return null;
  if (typeof x.nutzen !== "number" || !Number.isInteger(x.nutzen) || x.nutzen < 1 || x.nutzen > 5) return null;
  const beleg = x.beleg.trim();
  const b = belege.get(beleg);
  if (!b) return null;
  const titel = saeubereKurz(x.titel, TITEL_MAX);
  const beschreibung = saeubereKurz(x.beschreibung, BESCHREIBUNG_MAX);
  if (titel.length < 3 || beschreibung.length < 3) return null;
  // Privates (Prüfauflage 3): ganz verworfen — auch kein Hinweis.
  if (nenntPrivates(`${x.titel} ${x.beschreibung}`)) return null;
  if (nenntKlasseC(`${titel} ${beschreibung}`)) return "c";
  return {
    titel,
    beschreibung,
    klasse,
    aufwand: x.aufwand as Aufwand,
    nutzen: x.nutzen,
    beleg: beleg.slice(0, BELEG_MAX),
    quelle: b.quelle,
    belegZahl: b.zahl,
  };
}

/** Der Notschalter der Ticket-Anlage: nur "0" schaltet ab (Vorgabe an). */
export function ticketAnlageAus(env: Readonly<Record<string, string | undefined>>): boolean {
  return (env.SELBSTVERBESSERUNG_TICKET ?? "").trim() === "0";
}

/**
 * Die mitlaufende Marke je Vorschlag — vom Server aus dem Ticketstatus gerechnet, nie aus Text.
 *   erfasst         Ticket angelegt (offen) — „vom Schmied erfasst“
 *   in_arbeit       Ticket in Arbeit
 *   gebaut          Ticket erledigt (oder der Vorschlag selbst gebaut)
 *   zurueckgestellt Ticket übersprungen / abgelöst
 *   braucht_ja      Ja gesagt, aber Außenwirkung (Klasse C): kein Ticket, Martin muss noch einmal bestätigen
 */
export const MARKEN = ["erfasst", "in_arbeit", "gebaut", "zurueckgestellt", "braucht_ja"] as const;
export type Marke = (typeof MARKEN)[number];

export function markeAus(
  status: Status,
  ticketId: number | null,
  ticketStatus: string | null,
  klasseCText: boolean,
): Marke | null {
  if (status === "gebaut") return "gebaut";
  // Nein oder Später: nie „erfasst“ — das Ticket (falls da) ist zurückgenommen.
  if ((status === "nein" || status === "spaeter") && ticketId !== null) return "zurueckgestellt";
  if (ticketId !== null && ticketStatus) {
    if (ticketStatus === "resolved") return "gebaut";
    if (ticketStatus === "in_progress") return "in_arbeit";
    if (ticketStatus === "skipped" || ticketStatus === "superseded") return "zurueckgestellt";
    return "erfasst";
  }
  if (ticketId !== null) return "erfasst";
  if (status === "ja" && klasseCText) return "braucht_ja";
  return null;
}

/** Ein Eintrag, wie GET /api/selbstverbesserung ihn liefert. */
export interface SelbstverbesserungEintrag {
  id: number;
  datum: string;
  quelle: QuellArt;
  beleg: string;
  belegZahl: number | null;
  titel: string;
  beschreibung: string;
  klasse: Exclude<Klasse, "C">;
  aufwand: Aufwand;
  nutzen: number;
  status: Status;
  erstellt: string;
  geaendert: string;
  /** Nummer des angelegten Tickets (nach „Ja“), sonst null. */
  ticketId: number | null;
  /** Mitlaufende Marke aus dem Ticketstatus, sonst null. */
  marke: Marke | null;
}

/** Streng lesen (Seite und Skript). Was nicht passt, fällt heraus. Rein. */
export function eintragAus(roh: unknown): SelbstverbesserungEintrag | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  const str = (v: unknown, max: number): string | null => (typeof v === "string" && v.length <= max ? v : null);
  const id = typeof r.id === "number" && Number.isInteger(r.id) && r.id > 0 ? r.id : null;
  const datum = str(r.datum, 10);
  const titel = str(r.titel, TITEL_MAX);
  const beschreibung = str(r.beschreibung, BESCHREIBUNG_MAX);
  const beleg = str(r.beleg, BELEG_MAX);
  const erstellt = str(r.erstellt, 40);
  const geaendert = str(r.geaendert, 40);
  if (id === null || !datum || !titel || beschreibung === null || beleg === null || !erstellt || !geaendert) return null;
  if (!(QUELL_ARTEN as readonly unknown[]).includes(r.quelle)) return null;
  if (r.klasse !== "A" && r.klasse !== "B") return null;
  if (!(AUFWAENDE as readonly unknown[]).includes(r.aufwand)) return null;
  if (!(STATUS as readonly unknown[]).includes(r.status)) return null;
  if (typeof r.nutzen !== "number" || !Number.isInteger(r.nutzen) || r.nutzen < 1 || r.nutzen > 5) return null;
  const belegZahl = typeof r.belegZahl === "number" && Number.isFinite(r.belegZahl) ? r.belegZahl : null;
  const ticketId = typeof r.ticketId === "number" && Number.isInteger(r.ticketId) && r.ticketId > 0 ? r.ticketId : null;
  const marke = (MARKEN as readonly unknown[]).includes(r.marke) ? (r.marke as Marke) : null;
  return {
    id, datum, titel, beschreibung, beleg, belegZahl, erstellt, geaendert, ticketId, marke,
    quelle: r.quelle as QuellArt,
    klasse: r.klasse,
    aufwand: r.aufwand as Aufwand,
    nutzen: r.nutzen,
    status: r.status as Status,
  };
}
